/**
 * Server-authoritative timer display (FR-7.1). Shows the countdown derived from
 * {@link TimerState} timestamps (no client-side countdown authority — DESIGN §7).
 * A local 250ms interval only re-derives the display from server truth so the
 * number ticks smoothly; it never decides expiry.
 */

import { useEffect, useState } from 'react';
import type { TimerState } from '@jeopardy/shared';
import { liveRemainingSeconds, timerBand } from '../game/timer-display';

export interface TimerViewProps {
  timer: TimerState;
  /** Injectable clock for tests (defaults to Date.now). */
  now?: () => number;
}

export function TimerView({ timer, now = Date.now }: TimerViewProps): React.JSX.Element {
  const [, forceTick] = useState(0);

  useEffect(() => {
    if (!timer.isActive || timer.isPaused) return;
    const id = setInterval(() => forceTick((n) => n + 1), 250);
    return () => clearInterval(id);
  }, [timer.isActive, timer.isPaused, timer.startedAtEpochMs]);

  const remaining = liveRemainingSeconds(timer, now());
  const band = timerBand(remaining, timer);

  return (
    <div className={`timer timer--${band}`} data-testid="timer">
      <span className="timer__time" data-testid="timer-seconds">
        {remaining}
      </span>
      <span className="timer__label">
        {band === 'expired'
          ? "Time's up"
          : band === 'paused'
            ? 'Paused'
            : band === 'idle'
              ? 'Ready'
              : 'Seconds'}
      </span>
    </div>
  );
}
